/**
 * CLI Device Authorization Page
 * Displays OAuth 2.0 Device Flow authorization screen for AppAI CLI.
 */

import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Terminal,
  CheckCircle2,
  XCircle,
  AlertCircle,
  ShieldCheck,
  ArrowRight,
  LogOut,
  Sparkles,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import {
  checkDeviceCode,
  checkDeviceEligibility,
  authorizeDevice,
  denyDevice,
  type DeviceCheckResponse,
  type DeviceEligibilityResponse,
} from '../services/api';
import { SignInModal } from '../components/auth/SignInModal';
import { UpgradeModal } from '../components/subscription';

export const DeviceAuthPage: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, isAuthenticated, isAnonymous, isLoading: isAuthLoading, logout } = useAuth();

  const [userCodeInput, setUserCodeInput] = useState(
    searchParams.get('user_code')?.trim().toUpperCase() || ''
  );
  const [activeCode, setActiveCode] = useState(userCodeInput);

  const [checkingCode, setCheckingCode] = useState(false);
  const [codeInfo, setCodeInfo] = useState<DeviceCheckResponse | null>(null);
  const [checkError, setCheckError] = useState<string | null>(null);

  const [checkingEligibility, setCheckingEligibility] = useState(false);
  const [eligibility, setEligibility] = useState<DeviceEligibilityResponse | null>(null);
  const [entitlementError, setEntitlementError] = useState<string | null>(null);

  const [submitting, setSubmitting] = useState(false);
  const [authStatus, setAuthStatus] = useState<'idle' | 'authorized' | 'denied'>('idle');
  const [actionError, setActionError] = useState<string | null>(null);

  const [showSignInModal, setShowSignInModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  // Sync active code from URL
  useEffect(() => {
    const codeFromUrl = searchParams.get('user_code')?.trim().toUpperCase();
    if (codeFromUrl) {
      setUserCodeInput(codeFromUrl);
      setActiveCode(codeFromUrl);
    }
  }, [searchParams]);

  // Step 1: Validate device user_code
  useEffect(() => {
    if (!activeCode) {
      setCodeInfo(null);
      setCheckError(null);
      return;
    }

    let isMounted = true;
    const verifyCode = async () => {
      setCheckingCode(true);
      setCheckError(null);
      try {
        const info = await checkDeviceCode(activeCode);
        if (isMounted) {
          setCodeInfo(info);
        }
      } catch (err: unknown) {
        if (isMounted) {
          const apiErr = err as { response?: { data?: { message?: string } } };
          setCodeInfo(null);
          setCheckError(
            apiErr.response?.data?.message ||
              t('deviceAuth.invalidCodeMessage', {
                defaultValue:
                  'Device authorization code has expired or is invalid. Please check the code or run `appai login` again on your terminal.',
              })
          );
        }
      } finally {
        if (isMounted) {
          setCheckingCode(false);
        }
      }
    };

    verifyCode();
    return () => {
      isMounted = false;
    };
  }, [activeCode, t]);

  // Step 2: Once user is logged in & code is valid, verify user capability entitlement
  useEffect(() => {
    if (isAuthLoading || !isAuthenticated || isAnonymous || !codeInfo?.valid) {
      setEligibility(null);
      setEntitlementError(null);
      return;
    }

    let isMounted = true;
    const verifyEligibility = async () => {
      setCheckingEligibility(true);
      setEntitlementError(null);
      try {
        const result = await checkDeviceEligibility(codeInfo.user_code);
        if (isMounted) {
          setEligibility(result);
        }
      } catch (err: unknown) {
        if (isMounted) {
          const apiErr = err as {
            response?: {
              data?: {
                code?: string;
                error?: string;
                error_code?: string;
                message?: string;
              };
            };
          };
          const errCode =
            apiErr.response?.data?.code ||
            apiErr.response?.data?.error_code ||
            apiErr.response?.data?.error;
          const rawMsg = apiErr.response?.data?.message;

          const isEntitlementError =
            errCode === 'CODE_AGENT_ENTITLEMENT_REQUIRED' ||
            (rawMsg &&
              (rawMsg.toLowerCase().includes('code_agent') ||
                rawMsg.toLowerCase().includes('coding agent') ||
                rawMsg.toLowerCase().includes('code agent')));

          const errMsg = isEntitlementError
            ? t('deviceAuth.defaultUpgradeMessage', {
                defaultValue:
                  'Gói hiện tại không có chức năng coding agent, hãy upgrade lên gói cao hơn để tiếp tục sử dụng.',
              })
            : rawMsg ||
              t('deviceAuth.defaultUpgradeMessage', {
                defaultValue:
                  'Gói hiện tại không có chức năng coding agent, hãy upgrade lên gói cao hơn để tiếp tục sử dụng.',
              });
          setEntitlementError(errMsg);
        }
      } finally {
        if (isMounted) {
          setCheckingEligibility(false);
        }
      }
    };

    verifyEligibility();
    return () => {
      isMounted = false;
    };
  }, [isAuthLoading, isAuthenticated, isAnonymous, codeInfo, t]);

  const handleManualCodeSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (userCodeInput.trim()) {
      setActiveCode(userCodeInput.trim().toUpperCase());
    }
  };

  const handleAuthorize = async () => {
    if (!codeInfo) return;
    setSubmitting(true);
    setActionError(null);
    try {
      await authorizeDevice(codeInfo.user_code);
      setAuthStatus('authorized');
    } catch (err: unknown) {
      const apiErr = err as {
        response?: { data?: { code?: string; message?: string } };
      };
      const errCode = apiErr.response?.data?.code;
      if (errCode === 'CODE_AGENT_ENTITLEMENT_REQUIRED') {
        setActionError(
          t('deviceAuth.defaultUpgradeMessage', {
            defaultValue:
              'Gói hiện tại không có chức năng coding agent, hãy upgrade lên gói cao hơn để tiếp tục sử dụng.',
          })
        );
      } else {
        setActionError(
          apiErr.response?.data?.message ||
            t('deviceAuth.defaultErrorMessage', {
              defaultValue: 'An error occurred while authorizing the device. Please try again.',
            })
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeny = async () => {
    if (!codeInfo) return;
    setSubmitting(true);
    setActionError(null);
    try {
      await denyDevice(codeInfo.user_code);
      setAuthStatus('denied');
    } catch (err: unknown) {
      const apiErr = err as { response?: { data?: { message?: string } } };
      setActionError(
        apiErr.response?.data?.message ||
          t('deviceAuth.defaultDenyErrorMessage', {
            defaultValue: 'An error occurred while denying the session.',
          })
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-slate-50 px-4 py-12 transition-colors dark:bg-slate-950">
      <div className="w-full max-w-md overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl dark:border-slate-800 dark:bg-slate-900">
        {/* Card Header */}
        <div className="flex items-center gap-3 border-b border-slate-100 bg-gradient-to-r from-teal-500/10 via-emerald-500/10 to-transparent p-6 dark:border-slate-800">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-teal-500 text-white shadow-lg shadow-teal-500/20">
            <Terminal className="h-6 w-6" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-900 dark:text-white">
              {t('deviceAuth.title', { defaultValue: 'AppAI CLI Login' })}
            </h1>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {t('deviceAuth.subtitle', {
                defaultValue: 'Authorize Coding Agent access on your machine',
              })}
            </p>
          </div>
        </div>

        {/* Card Body */}
        <div className="space-y-6 p-6">
          {/* Status: Authorized */}
          {authStatus === 'authorized' && (
            <div className="space-y-4 py-6 text-center">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 text-emerald-600 dark:bg-emerald-950/60 dark:text-emerald-400">
                <CheckCircle2 className="h-10 w-10" />
              </div>
              <div className="space-y-1">
                <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                  {t('deviceAuth.authorizedTitle', { defaultValue: 'Authorization Successful!' })}
                </h2>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  {t('deviceAuth.authorizedDesc', {
                    defaultValue:
                      'You can now return to your terminal to start working with Coding Agent.',
                  })}
                </p>
              </div>
              <button
                onClick={() => navigate('/new-chat')}
                className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-5 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100"
              >
                {t('deviceAuth.goToChat', { defaultValue: 'Go to Chat Interface' })}
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          )}

          {/* Status: Denied */}
          {authStatus === 'denied' && (
            <div className="space-y-4 py-6 text-center">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-rose-100 text-rose-600 dark:bg-rose-950/60 dark:text-rose-400">
                <XCircle className="h-10 w-10" />
              </div>
              <div className="space-y-1">
                <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                  {t('deviceAuth.deniedTitle', { defaultValue: 'Authorization Denied' })}
                </h2>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  {t('deviceAuth.deniedDesc', {
                    defaultValue: 'The terminal login request has been safely cancelled.',
                  })}
                </p>
              </div>
              <button
                onClick={() => navigate('/new-chat')}
                className="inline-flex items-center gap-2 rounded-xl bg-slate-100 px-5 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
              >
                {t('deviceAuth.returnHome', { defaultValue: 'Return Home' })}
              </button>
            </div>
          )}

          {/* Status: Idle */}
          {authStatus === 'idle' && (
            <>
              {/* Form enter code if no code in URL */}
              {!activeCode && (
                <form onSubmit={handleManualCodeSubmit} className="space-y-4">
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">
                      {t('deviceAuth.manualPrompt', {
                        defaultValue: 'Enter the code shown in your Terminal:',
                      })}
                    </label>
                    <input
                      type="text"
                      placeholder={t('deviceAuth.codePlaceholder', {
                        defaultValue: 'e.g. ABCD-EFGH',
                      })}
                      value={userCodeInput}
                      onChange={(e) => setUserCodeInput(e.target.value.toUpperCase())}
                      className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-center font-mono text-lg uppercase tracking-widest text-slate-900 focus:outline-none focus:ring-2 focus:ring-teal-500 dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={!userCodeInput.trim()}
                    className="w-full rounded-xl bg-teal-600 py-2.5 text-sm font-medium text-white transition hover:bg-teal-700 disabled:opacity-50"
                  >
                    {t('deviceAuth.continue', { defaultValue: 'Continue' })}
                  </button>
                </form>
              )}

              {/* Checking code loading */}
              {checkingCode && (
                <div className="flex flex-col items-center justify-center space-y-3 py-8">
                  <div className="h-8 w-8 animate-spin rounded-full border-2 border-teal-500 border-t-transparent" />
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    {t('deviceAuth.checkingCode', {
                      defaultValue: 'Checking authorization code...',
                    })}
                  </p>
                </div>
              )}

              {/* Code check error */}
              {checkError && (
                <div className="flex items-start gap-3 rounded-xl border border-rose-200 bg-rose-50 p-4 dark:border-rose-900/60 dark:bg-rose-950/40">
                  <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-rose-600 dark:text-rose-400" />
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-rose-900 dark:text-rose-200">
                      {t('deviceAuth.invalidCodeTitle', { defaultValue: 'Invalid Code' })}
                    </p>
                    <p className="text-xs leading-relaxed text-rose-700 dark:text-rose-300">
                      {checkError}
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        setActiveCode('');
                        setUserCodeInput('');
                        setCheckError(null);
                      }}
                      className="mt-2 inline-block text-xs font-semibold text-rose-800 underline dark:text-rose-300"
                    >
                      {t('deviceAuth.enterDifferentCode', { defaultValue: 'Enter different code' })}
                    </button>
                  </div>
                </div>
              )}

              {/* Valid Code Info Card */}
              {codeInfo && !checkError && (
                <div className="space-y-5">
                  {/* User Code badge */}
                  <div className="flex flex-col items-center justify-center rounded-xl border border-slate-200 bg-slate-100 p-4 dark:border-slate-700/60 dark:bg-slate-800/60">
                    <span className="mb-1 text-xs font-medium text-slate-500 dark:text-slate-400">
                      {t('deviceAuth.deviceCodeLabel', { defaultValue: 'Connecting Device Code' })}
                    </span>
                    <span className="font-mono text-2xl font-bold tracking-widest text-teal-600 dark:text-teal-400">
                      {codeInfo.user_code}
                    </span>
                  </div>

                  {/* Authentication requirement */}
                  {(!isAuthenticated || isAnonymous) && !isAuthLoading && (
                    <div className="space-y-4">
                      <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 dark:border-amber-900/60 dark:bg-amber-950/40">
                        <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400" />
                        <div className="space-y-1">
                          <p className="text-sm font-medium text-amber-900 dark:text-amber-200">
                            {t('deviceAuth.loginRequiredTitle', { defaultValue: 'Login Required' })}
                          </p>
                          <p className="text-xs text-amber-700 dark:text-amber-300">
                            {t('deviceAuth.loginRequiredDesc', {
                              defaultValue:
                                'You must log in with an official account to authorize Coding Agent for the CLI.',
                            })}
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setShowSignInModal(true)}
                        className="w-full rounded-xl bg-teal-600 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-teal-700"
                      >
                        {t('deviceAuth.signInButton', { defaultValue: 'Log In Account' })}
                      </button>
                    </div>
                  )}

                  {/* Authenticated user checking eligibility */}
                  {isAuthenticated && !isAnonymous && checkingEligibility && (
                    <div className="flex items-center justify-center space-x-2 py-4 text-sm text-slate-500">
                      <div className="h-5 w-5 animate-spin rounded-full border-2 border-teal-500 border-t-transparent" />
                      <span>
                        {t('deviceAuth.checkingEligibility', {
                          defaultValue: 'Checking package entitlement...',
                        })}
                      </span>
                    </div>
                  )}

                  {/* Entitlement Error / Package Upgrade required */}
                  {isAuthenticated && !isAnonymous && entitlementError && (
                    <div className="space-y-4">
                      <div className="space-y-2 rounded-xl border border-amber-200 bg-amber-50 p-4 dark:border-amber-900/60 dark:bg-amber-950/40">
                        <div className="flex items-center gap-2 text-sm font-semibold text-amber-800 dark:text-amber-300">
                          <Sparkles className="h-4 w-4 text-amber-600" />
                          <span>
                            {t('deviceAuth.packageRequiredTitle', {
                              defaultValue: 'Gói cước chưa hỗ trợ Coding Agent',
                            })}
                          </span>
                        </div>
                        <p className="text-xs leading-relaxed text-amber-700 dark:text-amber-300/90">
                          {entitlementError}
                        </p>
                      </div>

                      <div className="flex flex-col gap-2">
                        <button
                          type="button"
                          onClick={() => setShowUpgradeModal(true)}
                          className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 py-2.5 text-sm font-medium text-white shadow-sm transition hover:from-amber-600 hover:to-orange-600"
                        >
                          <Sparkles className="h-4 w-4" />
                          {t('deviceAuth.upgradePackage', { defaultValue: 'Upgrade Package' })}
                        </button>
                        <button
                          type="button"
                          onClick={() => logout()}
                          className="w-full py-2 text-xs text-slate-500 transition hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                        >
                          {t('deviceAuth.signInDifferentAccount', {
                            defaultValue: 'Log in with a different account',
                          })}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Eligible & Ready to Authorize */}
                  {isAuthenticated && !isAnonymous && eligibility && !entitlementError && (
                    <div className="space-y-5">
                      {/* User Account Info */}
                      <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 p-3.5 dark:border-slate-800 dark:bg-slate-800/40">
                        <div className="flex items-center gap-3">
                          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-teal-600 text-sm font-bold text-white shadow">
                            {(eligibility.account.name ||
                              eligibility.account.email ||
                              'U')[0].toUpperCase()}
                          </div>
                          <div>
                            <p className="text-sm font-semibold leading-tight text-slate-900 dark:text-white">
                              {eligibility.account.name ||
                                t('deviceAuth.defaultUserName', { defaultValue: 'User' })}
                            </p>
                            <p className="text-xs text-slate-500 dark:text-slate-400">
                              {eligibility.account.email || user?.email}
                            </p>
                          </div>
                        </div>
                        <button
                          type="button"
                          title={t('deviceAuth.signOut', { defaultValue: 'Log Out' })}
                          onClick={() => logout()}
                          className="rounded-lg p-2 text-slate-400 transition hover:bg-slate-200 hover:text-slate-600 dark:hover:bg-slate-700 dark:hover:text-slate-200"
                        >
                          <LogOut className="h-4 w-4" />
                        </button>
                      </div>

                      {/* Capabilities granted */}
                      <div className="flex items-start gap-2.5 rounded-xl border border-emerald-200 bg-emerald-50 p-3.5 dark:border-emerald-900/50 dark:bg-emerald-950/30">
                        <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600 dark:text-emerald-400" />
                        <div className="space-y-0.5 text-xs text-emerald-800 dark:text-emerald-300">
                          <p className="font-semibold">
                            {t('deviceAuth.grantedAccessTitle', { defaultValue: 'Access Scope:' })}
                          </p>
                          <p>
                            {t('deviceAuth.grantedAccessDesc', {
                              defaultValue:
                                'Permission to connect and interact with Coding Agent via terminal. No password or personal data is shared.',
                            })}
                          </p>
                        </div>
                      </div>

                      {actionError && (
                        <p className="text-center text-xs text-rose-600 dark:text-rose-400">
                          {actionError}
                        </p>
                      )}

                      {/* Confirmation Buttons */}
                      <div className="flex items-center gap-3 pt-2">
                        <button
                          type="button"
                          disabled={submitting}
                          onClick={handleDeny}
                          className="flex-1 rounded-xl border border-slate-300 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-100 disabled:opacity-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
                        >
                          {t('deviceAuth.deny', { defaultValue: 'Deny' })}
                        </button>
                        <button
                          type="button"
                          disabled={submitting}
                          onClick={handleAuthorize}
                          className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-teal-600 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-teal-700 disabled:opacity-50"
                        >
                          {submitting ? (
                            <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                          ) : (
                            t('deviceAuth.confirmLogin', { defaultValue: 'Confirm Login' })
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Modals */}
      {showSignInModal && (
        <SignInModal
          isOpen={showSignInModal}
          onClose={() => setShowSignInModal(false)}
          returnTo={`/device?user_code=${activeCode}`}
        />
      )}

      {showUpgradeModal && (
        <UpgradeModal
          open={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
          title={t('deviceAuth.upgradeModalTitle', {
            defaultValue: 'Upgrade Package for Coding Agent',
          })}
          message={
            entitlementError ||
            t('deviceAuth.defaultUpgradeMessage', {
              defaultValue:
                'Your current package does not have Coding Agent enabled. Please choose an eligible package to continue using the CLI.',
            })
          }
        />
      )}
    </div>
  );
};
